import PropTypes from 'prop-types'
import { useState } from 'react'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { formatearFecha } from '../helpers/helper'
import { FechaSelect } from '../reutilizables/fecha/FechaSelect'
import { actualizarItem, actualizarItemsMultiples } from '../servicios/pedidosService'
import * as ventasService from '../servicios/ventasService'
import Select from '../ui/Select'
import styles from './DetalleEstadoPedido.module.css'

const STATUS_OK = 200
const ESTADO_ENVIADO = 1
const ESTADO_ENTREGADO = 2
const ESTADO_ENTREGA_OBSERVADA = 3

const ESTADOS_FECHA = {
  [ESTADO_ENVIADO]: 'fechaDespachado',
  [ESTADO_ENTREGADO]: 'fechaRecibido',
  [ESTADO_ENTREGA_OBSERVADA]: 'fechaRecibido'
}

export const DetalleEstadoPedido = ({ pedido, estados, onActualizar }) => {
  const [estado, setEstado] = useState(pedido.idPedidoItemEstado || 0)
  const [fecha, setFecha] = useState({})
  const [errorFecha, setErrorFecha] = useState(false)
  const [errorMensaje, setErrorMensaje] = useState('')
  const [mostrarModal, setMostrarModal] = useState(false)

  const estadosSeleccionables = estados.filter((estadoItem) => !estadoItem.esAutomatico)

  const opcionesEstado = estadosSeleccionables.map((estadoItem) => ({
    value: estadoItem.idPedidoItemEstado,
    label: estadoItem.nombre
  }))

  const handleChange = ({ value }) => {
    const estadoNumerico = parseInt(value, 10)
    setFecha({})
    setErrorMensaje('')
    setEstado(estadoNumerico)
  }

  const obtenerInfoEstado = (estadoId) => {
    const estadoNumerico = Number(estadoId)

    const estadoSeleccionado = estados.find(
      (op) => op.idPedidoItemEstado === estadoNumerico
    )

    const campoFecha = ESTADOS_FECHA[estadoNumerico] || null
    const requiereFecha = Boolean(campoFecha)

    const claveFecha = `${pedido.idPedidoItem || pedido.idPedido}-${estadoNumerico}`

    const fechaOriginalEstado = pedido[campoFecha]

    const fechaBase =
      fecha[claveFecha] || validarFecha(fechaOriginalEstado, pedido.fechaPedido)

    return {
      estadoNumerico,
      estadoLabel: estadoSeleccionado?.nombre || '',
      requiereFecha,
      campoFecha,
      claveFecha,
      fechaBase
    }
  }

  const guardar = async () => {
    try {
      const { estadoNumerico, requiereFecha, campoFecha, fechaBase } =
        obtenerInfoEstado(estado)

      if (requiereFecha) {
        if (!fechaBase) {
          setErrorFecha(true)
          setErrorMensaje('Debe seleccionar una fecha antes de guardar.')
          return
        }

        const fechaBaseYMD = (fechaBase || '').split('T')[0]
        const fechaPedidoYMD = (pedido.fechaPedido || '').split('T')[0]
        if (new Date(fechaBaseYMD) < new Date(fechaPedidoYMD)) {
          setErrorFecha(true)
          setErrorMensaje(
            'La fecha seleccionada no puede ser anterior a la fecha del pedido.'
          )
          return
        }

        if (campoFecha === 'fechaRecibido' && pedido.fechaDespachado) {
          const fechaEnviadoYMD = (pedido.fechaDespachado || '').split('T')[0]
          const fechaEntregadoYMD = (fechaBase || '').split('T')[0]
          if (new Date(fechaEntregadoYMD) < new Date(fechaEnviadoYMD)) {
            setErrorFecha(true)
            setErrorMensaje('La fecha de entrega debe ser posterior a la fecha de envío.')
            return
          }
        }
      }

      setErrorFecha(false)

      const buildFechas = (origen = {}) => ({
        fechaDespachado:
          campoFecha === 'fechaDespachado'
            ? formatearFecha(
                fechaBase || origen.fechaDespachado || pedido.fechaDespachado,
                'ISO'
              )
            : null,
        fechaRecibido:
          campoFecha === 'fechaRecibido'
            ? formatearFecha(
                fechaBase || origen.fechaRecibido || pedido.fechaRecibido,
                'ISO'
              )
            : null
      })

      const construirPedidoItem = (item) => ({
        idPedidoItem: item.idPedidoItem || null,
        idPedido: item.idPedido,
        idCategoria: item.idCategoria || 0,
        idPublicacion: item.idPublicacion || 0,
        idVendedor: item.idVendedor || 0,
        titulo: item.titulo || '',
        descripcion: item.descripcion || '',
        precioUnitario: item.precioUnitario || 0,
        idMoneda: item.idMoneda || 1,
        cantidad: item.cantidad || 0,
        idMedioPago: item.idMedioPago || 0,
        numComprobantePago: item.numComprobantePago || '',
        idMedioEnvio: item.idMedioEnvio || 1,
        entregaLocalidad: Number(item.entregaLocalidad) || 0,
        entregaLocalidadDesc: item.entregaLocalidadDesc || '',
        entregaCalle: item.entregaCalle || '',
        entregaCalleAltura: item.entregaCalleAltura || '',
        entregaCP: item.entregaCP || '',
        entregaDpto: item.entregaDpto || '',
        entregaPiso: item.entregaPiso || '',
        idPedidoItemEstado: estadoNumerico,
        ...buildFechas(item)
      })

      const ventasResponse = await ventasService.buscarPorUsuario(pedido.idVendedor, '')
      const pedidoVenta = ventasResponse?.data?.find(
        (p) => p.idPedido === pedido.idPedido
      )
      const itemsDelPedido = pedidoVenta?.ventaItems || []

      let respuesta

      if (itemsDelPedido.length > 1) {
        const payloadBatch = itemsDelPedido.map((it) => construirPedidoItem(it))
        respuesta = await actualizarItemsMultiples(payloadBatch)
      } else {
        const pedidoItemSingle = construirPedidoItem(pedido)
        respuesta = await actualizarItem(pedidoItemSingle)
      }

      if (respuesta?.ok || respuesta?.status === STATUS_OK) {
        setMostrarModal(true)
        onActualizar?.()
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al guardar el estado.`
      toastErrorLog(error, mensaje, 'general')
    }
  }

  const guardarFecha = (nuevaFecha, clave) => {
    setFecha((prev) => ({ ...prev, [clave]: nuevaFecha }))
    setErrorFecha(false)
  }

  const validarFecha = (fechaEstado, fechaPedido) => {
    if (!fechaEstado) {
      return ''
    }
    return new Date(fechaEstado) > new Date(fechaPedido) ? fechaEstado.split('T')[0] : ''
  }

  const { estadoLabel, requiereFecha, claveFecha, fechaBase } = obtenerInfoEstado(estado)

  return (
    <div className={styles.detallePedido}>
      <h3 className={styles.tituloEstado}>Estado del pedido</h3>
      <p className={styles.estadoActual}>Estado actual:</p>
      <div>
        <Select
          name="estado"
          value={estado}
          onChange={({ target }) => handleChange(target)}
          datos={opcionesEstado}
          optionMetaDatos={{ value: 'value', text: 'label' }}
          className={styles.selectEstado}
        />
        {requiereFecha && (
          <FechaSelect
            label={estadoLabel}
            claveFecha={claveFecha}
            errorFecha={errorFecha}
            errorMensaje={errorMensaje}
            fecha={fechaBase}
            guardarFecha={guardarFecha}
          />
        )}

        <div>
          <button className={styles.buttonGuardarEstado} onClick={guardar}>
            Guardar
          </button>
        </div>
      </div>
      {mostrarModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h4>Estado guardado exitosamente</h4>
            <button className={styles.modalButton} onClick={() => setMostrarModal(false)}>
              Aceptar
            </button>
          </div>
        </div>
      )}

      <hr />
    </div>
  )
}

DetalleEstadoPedido.propTypes = {
  pedido: PropTypes.shape({
    idPedidoItem: PropTypes.number.isRequired,
    idPedido: PropTypes.number.isRequired,
    idCategoria: PropTypes.number,
    idPublicacion: PropTypes.number,
    idVendedor: PropTypes.number,
    titulo: PropTypes.string,
    descripcion: PropTypes.string,
    precioUnitario: PropTypes.number,
    idMoneda: PropTypes.number,
    cantidad: PropTypes.number,
    idMedioPago: PropTypes.number,
    numComprobantePago: PropTypes.string,
    idMedioEnvio: PropTypes.number,
    entregaLocalidad: PropTypes.number,
    entregaLocalidadDesc: PropTypes.string,
    entregaCalle: PropTypes.string,
    entregaCalleAltura: PropTypes.string,
    entregaCP: PropTypes.string,
    entregaDpto: PropTypes.string,
    entregaPiso: PropTypes.string,
    idPedidoItemEstado: PropTypes.number,
    fechaDespachado: PropTypes.string,
    fechaRecibido: PropTypes.string,
    fechaPedido: PropTypes.string.isRequired
  }).isRequired,
  estados: PropTypes.arrayOf(
    PropTypes.shape({
      idPedidoItemEstado: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
      esAutomatico: PropTypes.bool
    })
  ).isRequired,
  onActualizar: PropTypes.func
}
